import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { DocumentNumber } from './document-number'
import type { ExpenseCategory } from './expense-category'
import type * as payment from './payment'
import type { ProjectId } from './project-id'
import type { Subaccount } from './subaccount'

/**
 * Normalized JavaScript representation of the BCH Defend to Spend workbooks.
 *
 * This type covers the parsing/normalization part of the
 * `BCH Defend to Spend Outline`: convert the source spreadsheets into stable
 * JavaScript rows that can be joined by project, document, and payment
 * identifiers instead of by direct Excel cell references.
 *
 * Source files and tabs:
 * - `Cash Draw_Period_8_2026.xlsx`
 *   - `PACCT`: project-level metadata used to resolve `Document #`, `OH Rate`,
 *     `Project End Date`, and `Effective Date`
 *   - `PACCT Pivot`: document-level `Sum of Drawdown Amount`
 *   - `PMS Upload`: target rows identified by Excel row number,
 *     `Payment Type`, and `Subacct Code`
 * - `Justifications_May2026.xlsx`
 *   - `Current Mid-Month`, `Prior Month End`, and `Prior Mid-Month`: wide
 *     project-by-expense-category amount sheets
 *   - The outline's `New Justifications` step is represented by
 *     `justification.Lookup`, calculated in memory after parsing, and is not
 *     expected in this source workbook
 * - `subaccount_summary_-_April 2026.csv`: award metadata keyed by
 *   `DOCUMENT NUMBER`, including `SUBACCOUNT` and `AWARD END DATE`
 *
 * Normalized rows preserve the outline's join points:
 * - project references: `ProjectID` / `All Projects`
 * - document references: `Document #`, `Row Labels`, and `DOCUMENT NUMBER`
 * - payment references: `Payment Type`, `Subacct Code`, and PMS Upload row
 *   number
 * - amount references: expense category columns and `Sum of Drawdown Amount`
 *
 * This is the input shape for the outline's balance formula:
 *
 * ```ts
 * currentMonthBalance =
 *   priorMonthEnd - priorMidMonth + currentMidMonth
 * ```
 */
export interface WorkbookData {
  readonly cashDraw: WorkbookData.CashDraw
  readonly justifications: WorkbookData.Justifications
  readonly subaccountSummary: readonly WorkbookData.SubaccountSummaryRow[]
}
export namespace WorkbookData {
  /**
   * Parsed workbook sections before they are joined into one BCH workbook data
   * aggregate.
   */
  export interface Parts {
    readonly cashDraw: CashDraw
    readonly justifications: Justifications
    readonly subaccountSummary: readonly SubaccountSummaryRow[]
  }
  export namespace Parts {
    export const isParts: TypeChecker<Parts> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object({
        cashDraw: CashDraw.isCashDraw,
        justifications: Justifications.isJustifications,
        subaccountSummary: TypeChecker.Factory.list(
          SubaccountSummaryRow.isSubaccountSummaryRow,
        ),
      }).usingAlias('WorkbookData.Parts'),
    )
  }

  /**
   * One row read from a BCH source worksheet.
   *
   * Each array index is the zero-based worksheet column position. Cell values
   * stay `unknown` here because SheetJS can produce strings, numbers, booleans,
   * dates, nulls, or blanks before BCH-specific parsers normalize each column.
   */
  export type SheetRow = readonly unknown[]
  export namespace SheetRow {
    export const isSheetRow: TypeChecker<SheetRow> =
      TypeChecker.isReadonlyArray.usingAlias('WorkbookData.SheetRow')
  }

  /**
   * BCH source worksheets needed to build normalized workbook data.
   */
  export interface SourceSheets {
    readonly cashDraw: CashDraw.Worksheets
    readonly justifications: Justifications.Worksheets
    readonly subaccountSummary: readonly SheetRow[]
  }
  export namespace SourceSheets {
    /**
     * Literal worksheet names expected in BCH source workbooks.
     */
    export const NAMES = {
      cashDraw: {
        pacct: 'PACCT',
        pacctPivot: 'PACCT Pivot',
        pmsUpload: 'PMS Upload',
      },
      justifications: {
        currentMidMonth: 'Current Mid-Month',
        priorMidMonth: 'Prior Mid-Month',
        priorMonthEnd: 'Prior Month End',
      },
    } as const

    export const isSourceSheets: TypeChecker<SourceSheets> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object({
          cashDraw: CashDraw.Worksheets.isWorksheets,
          justifications: Justifications.Worksheets.isWorksheets,
          subaccountSummary: TypeChecker.Factory.list(SheetRow.isSheetRow),
        }).usingAlias('WorkbookData.SourceSheets'),
      )
    export const assertIsSourceSheets: TypeAsserter<SourceSheets> =
      TypeAsserter.Factory.from(isSourceSheets)

    /**
     * Checks BCH worksheet names using the same loose matching used for source
     * workbook parsing.
     */
    export function hasName(name: string, expectedName: string) {
      return normalizeName(name) === normalizeName(expectedName)
    }

    /**
     * Reduces a BCH worksheet name to a stable comparison key.
     */
    function normalizeName(name: string) {
      return name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
    }
  }

  /**
   * Checks BCH workbook header names using the same loose matching used by
   * parsers and workbook writers.
   */
  export function hasHeaderName(value: unknown, expectedName: string) {
    return canonicalize(value) === canonicalize(expectedName)
  }

  const isRaw = TypeChecker.Factory.record(
    TypeChecker.isString,
    TypeChecker.isUnknown,
  )

  export const isWorkbookData: TypeChecker<WorkbookData> =
    TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object({
        cashDraw: CashDraw.isCashDraw,
        justifications: Justifications.isJustifications,
        subaccountSummary: TypeChecker.Factory.list(
          SubaccountSummaryRow.isSubaccountSummaryRow,
        ),
      }),
    )

  /**
   * Builds a BCH workbook data aggregate from already-normalized workbook
   * sections.
   */
  export function from(parts: Readonly<Parts>): WorkbookData {
    return {
      cashDraw: {
        pacct: [...parts.cashDraw.pacct],
        pacctPivot: [...parts.cashDraw.pacctPivot],
        pmsUpload: [...parts.cashDraw.pmsUpload],
      },
      justifications: {
        currentMidMonth: [...parts.justifications.currentMidMonth],
        priorMidMonth: [...parts.justifications.priorMidMonth],
        priorMonthEnd: [...parts.justifications.priorMonthEnd],
      },
      subaccountSummary: [...parts.subaccountSummary],
    }
  }

  /**
   * Builds normalized BCH workbook data from worksheet rows.
   */
  export function fromSheets(sheets: Readonly<SourceSheets>): WorkbookData {
    SourceSheets.assertIsSourceSheets(sheets)
    return from({
      cashDraw: CashDraw.fromWorksheets(sheets.cashDraw),
      justifications: Justifications.fromWorksheets(sheets.justifications),
      subaccountSummary: SubaccountSummaryRow.fromSheet(
        sheets.subaccountSummary,
      ),
    })
  }

  export interface CashDraw {
    readonly pacct: readonly PacctRow[]
    readonly pacctPivot: readonly PacctPivotRow[]
    readonly pmsUpload: readonly PmsUploadRow[]
  }
  export namespace CashDraw {
    /**
     * Cash draw workbook worksheets needed by the BCH workflow.
     */
    export interface Worksheets {
      readonly pacct: readonly SheetRow[]
      readonly pacctPivot: readonly SheetRow[]
      /**
       * Optional: only the `PMS Upload` writer reads these rows, so a draw file
       * without the tab still supports building the justifications workbook.
       */
      readonly pmsUpload?: readonly SheetRow[]
    }
    export namespace Worksheets {
      export const isWorksheets: TypeChecker<Worksheets> =
        TypeChecker.Factory.object({
          pacct: TypeChecker.Factory.list(SheetRow.isSheetRow),
          pacctPivot: TypeChecker.Factory.list(SheetRow.isSheetRow),
          pmsUpload: TypeChecker.Factory.optional(
            TypeChecker.Factory.list(SheetRow.isSheetRow),
          ),
        }).usingAlias('WorkbookData.CashDraw.Worksheets')
    }

    export const isCashDraw: TypeChecker<CashDraw> = TypeChecker.Factory.lazy(
      () =>
        TypeChecker.Factory.object({
          pacct: TypeChecker.Factory.list(PacctRow.isPacctRow),
          pacctPivot: TypeChecker.Factory.list(PacctPivotRow.isPacctPivotRow),
          pmsUpload: TypeChecker.Factory.list(PmsUploadRow.isPmsUploadRow),
        }),
    )

    /**
     * Builds the normalized cash draw section from source worksheet rows.
     */
    export function fromWorksheets(sheets: Readonly<Worksheets>): CashDraw {
      return {
        pacct: PacctRow.fromSheet(sheets.pacct),
        pacctPivot: PacctPivotRow.fromSheet(sheets.pacctPivot),
        pmsUpload: sheets.pmsUpload
          ? PmsUploadRow.fromSheet(sheets.pmsUpload)
          : [],
      }
    }
  }

  export interface Justifications {
    readonly currentMidMonth: readonly ExpenseRow[]
    readonly priorMonthEnd: readonly ExpenseRow[]
    readonly priorMidMonth: readonly ExpenseRow[]
  }
  export namespace Justifications {
    /**
     * Justifications workbook worksheets needed by the BCH workflow.
     */
    export interface Worksheets {
      readonly currentMidMonth: readonly SheetRow[]
      readonly priorMonthEnd: readonly SheetRow[]
      readonly priorMidMonth: readonly SheetRow[]
    }
    export namespace Worksheets {
      export const isWorksheets: TypeChecker<Worksheets> =
        TypeChecker.Factory.object({
          currentMidMonth: TypeChecker.Factory.list(SheetRow.isSheetRow),
          priorMidMonth: TypeChecker.Factory.list(SheetRow.isSheetRow),
          priorMonthEnd: TypeChecker.Factory.list(SheetRow.isSheetRow),
        }).usingAlias('WorkbookData.Justifications.Worksheets')
    }

    export const isJustifications: TypeChecker<Justifications> =
      TypeChecker.Factory.lazy(() =>
        TypeChecker.Factory.object({
          currentMidMonth: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
          priorMidMonth: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
          priorMonthEnd: TypeChecker.Factory.list(ExpenseRow.isExpenseRow),
        }),
      )

    /**
     * Builds the normalized justifications section from source worksheet rows.
     */
    export function fromWorksheets(
      sheets: Readonly<Worksheets>,
    ): Justifications {
      return {
        currentMidMonth: ExpenseRow.fromSheet(sheets.currentMidMonth),
        priorMidMonth: ExpenseRow.fromSheet(sheets.priorMidMonth),
        priorMonthEnd: ExpenseRow.fromSheet(sheets.priorMonthEnd),
      }
    }
  }

  export interface ExpenseRow {
    readonly projectId: ProjectId
    readonly expenseCategory: ExpenseCategory
    readonly amount: number
    readonly raw?: Record<string, unknown>
  }
  export namespace ExpenseRow {
    export const isExpenseRow: TypeChecker<ExpenseRow> =
      TypeChecker.Factory.object({
        amount: TypeChecker.isNumber,
        expenseCategory: TypeChecker.isString,
        projectId: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(isRaw),
      })

    /**
     * Parses a BCH wide expense worksheet into project/category balances.
     */
    export function fromSheet(rows: readonly SheetRow[]): ExpenseRow[] {
      const headerIndex = findHeaderRow(rows, ['All Projects'])
      const header = rows[headerIndex] ?? []
      const projectCol = findColumn(header, ['All Projects'])
      // BCH expense sheets are wide: each column after project id is an
      // expense category that becomes its own normalized row.
      const categoryColumns = header
        .map((cell, column) => ({ category: normalizeText(cell), column }))
        .filter(
          ({ category, column }) => Boolean(category) && column > projectCol,
        ) as { readonly category: string; readonly column: number }[]

      return rows.slice(headerIndex + 1).flatMap(row => {
        const projectId = normalizeText(row[projectCol])
        if (!projectId || canonicalize(projectId) === 'total') {
          return []
        }

        const raw = toRawRow(header, row)
        return categoryColumns.map(({ category, column }) => ({
          amount: parseNumber(row[column]),
          expenseCategory: category,
          projectId,
          raw,
        }))
      })
    }
  }

  export interface PacctRow {
    readonly projectId: ProjectId
    readonly documentNumber?: DocumentNumber
    readonly ohRate?: number
    readonly projectEndDate?: string
    readonly effectiveDate?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace PacctRow {
    export const isPacctRow: TypeChecker<PacctRow> = TypeChecker.Factory.object(
      {
        documentNumber: TypeChecker.Factory.optional(TypeChecker.isString),
        effectiveDate: TypeChecker.Factory.optional(TypeChecker.isString),
        ohRate: TypeChecker.Factory.optional(TypeChecker.isNumber),
        projectEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
        projectId: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(isRaw),
      },
    )

    /**
     * Parses the cash draw PACCT worksheet.
     */
    export function fromSheet(rows: readonly SheetRow[]): PacctRow[] {
      const headerIndex = findHeaderRow(rows, [
        'ProjectID',
        'Document #',
        'OH Rate',
      ])
      const header = rows[headerIndex] ?? []
      const projectCol = findColumn(header, ['ProjectID'])
      const documentCol = findColumn(header, ['Document #'])
      const documentNumberCol = findOptionalColumn(header, ['Document Number'])
      const ohRateCol = findOptionalColumn(header, ['OH Rate'])
      const projectEndDateCol = findOptionalColumn(header, ['Project End Date'])
      const effectiveDateCol = findOptionalColumn(header, ['Effective Date'])

      return rows
        .slice(headerIndex + 1)
        .map(row => {
          const projectId = normalizeText(row[projectCol])
          if (!projectId) {
            return undefined
          }
          return {
            documentNumber:
              normalizeText(row[documentCol]) ??
              valueAt(row, documentNumberCol, normalizeText),
            effectiveDate: valueAt(row, effectiveDateCol, normalizeDate),
            ohRate: valueAt(row, ohRateCol, parseOptionalNumber),
            projectEndDate: valueAt(row, projectEndDateCol, normalizeDate),
            projectId,
            raw: toRawRow(header, row),
          } satisfies PacctRow
        })
        .filter(isDefined)
    }
  }

  export interface PacctPivotRow {
    readonly documentNumber: DocumentNumber
    readonly drawdownAmount: number
    readonly raw?: Record<string, unknown>
  }
  export namespace PacctPivotRow {
    export const isPacctPivotRow: TypeChecker<PacctPivotRow> =
      TypeChecker.Factory.object({
        documentNumber: TypeChecker.isString,
        drawdownAmount: TypeChecker.isNumber,
        raw: TypeChecker.Factory.optional(isRaw),
      })

    /**
     * Parses the cash draw PACCT Pivot worksheet.
     */
    export function fromSheet(rows: readonly SheetRow[]): PacctPivotRow[] {
      const headerIndex = findHeaderRow(rows, [
        'Row Labels',
        'Sum of Drawdown Amount',
      ])
      const header = rows[headerIndex] ?? []
      const documentCol = findColumn(header, ['Row Labels'])
      const drawdownAmountCol = findColumn(header, ['Sum of Drawdown Amount'])

      return rows
        .slice(headerIndex + 1)
        .map(row => {
          const documentNumber = normalizeText(row[documentCol])
          if (
            !documentNumber ||
            canonicalize(documentNumber) === 'grandtotal'
          ) {
            return undefined
          }
          return {
            documentNumber,
            drawdownAmount: parseNumber(row[drawdownAmountCol]),
            raw: toRawRow(header, row),
          } satisfies PacctPivotRow
        })
        .filter(isDefined)
    }
  }

  export interface PmsUploadRow {
    readonly rowIndex: number
    readonly paymentType?: payment.Type
    readonly subacctCode?: Subaccount.Code
    readonly paymentJustification?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace PmsUploadRow {
    /**
     * PMS Upload column names used by parsing and workbook population.
     */
    export const HEADERS = {
      paymentJustification: 'Payment Justification (Maximum 1000 Characters)',
      paymentType: 'Payment Type',
      subacctCode: 'Subacct Code',
    } as const

    export const isPmsUploadRow: TypeChecker<PmsUploadRow> =
      TypeChecker.Factory.object({
        paymentJustification: TypeChecker.Factory.optional(
          TypeChecker.isString,
        ),
        paymentType: TypeChecker.Factory.optional(TypeChecker.isString),
        raw: TypeChecker.Factory.optional(isRaw),
        rowIndex: TypeChecker.isNumber,
        subacctCode: TypeChecker.Factory.optional(TypeChecker.isString),
      })

    /**
     * Parses the cash draw PMS Upload worksheet.
     */
    export function fromSheet(rows: readonly SheetRow[]): PmsUploadRow[] {
      const headerIndex = findHeaderRow(rows, [
        HEADERS.subacctCode,
        HEADERS.paymentType,
      ])
      const header = rows[headerIndex] ?? []
      const subacctCodeCol = findOptionalColumn(header, [HEADERS.subacctCode])
      const paymentTypeCol = findOptionalColumn(header, [HEADERS.paymentType])
      const paymentJustificationCol = findOptionalColumn(header, [
        HEADERS.paymentJustification,
      ])

      return rows
        .slice(headerIndex + 1)
        .map((row, index) => {
          const paymentType = valueAt(row, paymentTypeCol, normalizeText)
          const subacctCode = valueAt(row, subacctCodeCol, normalizeText)
          if (!paymentType && !subacctCode) {
            return undefined
          }
          return {
            paymentJustification: valueAt(
              row,
              paymentJustificationCol,
              normalizeText,
            ),
            paymentType,
            raw: toRawRow(header, row),
            // SheetJS rows are zero-based here; PMS upload row ids need the
            // one-based Excel row number after the header row.
            rowIndex: headerIndex + index + 2,
            subacctCode,
          } satisfies PmsUploadRow
        })
        .filter(isDefined)
    }
  }

  export interface SubaccountSummaryRow {
    readonly documentNumber: DocumentNumber
    readonly subaccount?: Subaccount
    readonly awardEndDate?: string
    readonly raw?: Record<string, unknown>
  }
  export namespace SubaccountSummaryRow {
    export const isSubaccountSummaryRow: TypeChecker<SubaccountSummaryRow> =
      TypeChecker.Factory.object({
        awardEndDate: TypeChecker.Factory.optional(TypeChecker.isString),
        documentNumber: TypeChecker.isString,
        raw: TypeChecker.Factory.optional(isRaw),
        subaccount: TypeChecker.Factory.optional(TypeChecker.isString),
      })

    /**
     * Parses the BCH subaccount summary export.
     */
    export function fromSheet(
      rows: readonly SheetRow[],
    ): SubaccountSummaryRow[] {
      const headerIndex = findHeaderRow(rows, ['DOCUMENT NUMBER'])
      const header = rows[headerIndex] ?? []
      const documentCol = findColumn(header, ['DOCUMENT NUMBER'])
      const subaccountCol = findOptionalColumn(header, ['SUBACCOUNT'])
      const awardEndDateCol = findOptionalColumn(header, ['AWARD END DATE'])

      return rows
        .slice(headerIndex + 1)
        .map(row => {
          const documentNumber = normalizeText(row[documentCol])
          if (!documentNumber) {
            return undefined
          }
          return {
            awardEndDate: valueAt(row, awardEndDateCol, normalizeDate),
            documentNumber,
            raw: toRawRow(header, row),
            subaccount: valueAt(row, subaccountCol, normalizeText),
          } satisfies SubaccountSummaryRow
        })
        .filter(isDefined)
    }
  }

  /**
   * Finds the first worksheet row that contains all required headers.
   */
  function findHeaderRow(
    rows: readonly SheetRow[],
    requiredHeaders: readonly string[],
  ) {
    const required = requiredHeaders.map(canonicalize)
    const headerIndex = rows.findIndex(row => {
      // Header matching ignores punctuation/case because the BCH source files
      // are human-maintained and can vary slightly.
      const available = new Set(row.map(cell => canonicalize(cell)))
      return required.every(header => available.has(header))
    })
    if (headerIndex === -1) {
      throw new Error(
        `BCH workbook sheet is missing required headers: ${requiredHeaders.join(
          ', ',
        )}`,
      )
    }
    return headerIndex
  }

  /**
   * Finds a required column by any accepted header name.
   */
  function findColumn(header: SheetRow, names: readonly string[]) {
    const column = findOptionalColumn(header, names)
    if (column === undefined) {
      throw new Error(
        `BCH workbook sheet is missing column: ${names.join(' / ')}`,
      )
    }
    return column
  }

  /**
   * Finds an optional column by any accepted header name.
   */
  function findOptionalColumn(header: SheetRow, names: readonly string[]) {
    const column = header.findIndex(cell =>
      names.some(name => hasHeaderName(cell, name)),
    )
    return column === -1 ? undefined : column
  }

  /**
   * Builds a raw header-to-value snapshot for diagnostics and later lookup
   * logic.
   */
  function toRawRow(header: SheetRow, row: SheetRow): Record<string, unknown> {
    const raw: Record<string, unknown> = {}
    const counts = new Map<string, number>()
    for (const [index, headerCell] of header.entries()) {
      const key = normalizeText(headerCell)
      if (!key) {
        continue
      }
      const count = counts.get(key) ?? 0
      counts.set(key, count + 1)
      // Duplicate headers are preserved with a numeric suffix instead of
      // overwriting earlier values.
      raw[count === 0 ? key : `${key} ${count + 1}`] = row[index] ?? null
    }
    return raw
  }

  /**
   * Parses a cell only when its column exists.
   */
  function valueAt<T>(
    row: SheetRow,
    column: number | undefined,
    parse: (value: unknown) => T | undefined,
  ) {
    return column === undefined ? undefined : parse(row[column])
  }

  /**
   * Normalizes a cell into trimmed text, treating workbook placeholder blanks
   * as missing values.
   */
  function normalizeText(value: unknown) {
    const stringValue = toCellString(value)
    if (!stringValue) {
      return undefined
    }
    const text = stringValue.replace(/\s+/g, ' ').trim()
    if (!text || text === '-' || text === '-   ') {
      return undefined
    }
    return text
  }

  /**
   * Parses a cell amount and defaults missing/non-numeric cells to zero.
   */
  function parseNumber(value: unknown) {
    const parsed = parseOptionalNumber(value)
    return parsed ?? 0
  }

  /**
   * Parses a cell amount while preserving missing/non-numeric cells as
   * undefined.
   */
  function parseOptionalNumber(value: unknown) {
    if (typeof value === 'number') {
      return value
    }

    const text = normalizeText(value)
    if (!text) {
      return undefined
    }

    const compact = text.replace(/[$,\s]/g, '')
    if (!compact || compact === '-') {
      return undefined
    }

    // Accounting exports often represent negative amounts as "(123.45)".
    const isNegative = compact.startsWith('(') && compact.endsWith(')')
    const numericText = compact.replace(/[()]/g, '')
    const parsed = Number(numericText)
    if (!Number.isFinite(parsed)) {
      return undefined
    }
    return isNegative ? -parsed : parsed
  }

  /**
   * Normalizes dates into ISO date strings when they are recognizable.
   */
  function normalizeDate(value: unknown) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return toIsoDate(value)
    }

    if (typeof value === 'number') {
      // Excel serial dates use 1899-12-30 as the JavaScript-compatible epoch.
      return toIsoDate(new Date(Date.UTC(1899, 11, 30 + value)))
    }

    const text = normalizeText(value)
    if (!text) {
      return undefined
    }

    const match = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(text)
    if (!match) {
      return text
    }

    const month = Number(match[1])
    const day = Number(match[2])
    const rawYear = Number(match[3])
    const year =
      rawYear < 100 ? (rawYear < 50 ? 2000 + rawYear : 1900 + rawYear) : rawYear
    return [
      String(year).padStart(4, '0'),
      String(month).padStart(2, '0'),
      String(day).padStart(2, '0'),
    ].join('-')
  }

  /**
   * Formats a Date as `YYYY-MM-DD` using UTC fields.
   */
  function toIsoDate(date: Date) {
    return [
      String(date.getUTCFullYear()).padStart(4, '0'),
      String(date.getUTCMonth() + 1).padStart(2, '0'),
      String(date.getUTCDate()).padStart(2, '0'),
    ].join('-')
  }

  /**
   * Produces a loose comparison key for workbook headers and sentinel values.
   */
  function canonicalize(value: unknown) {
    return (toCellString(value) ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
  }

  /**
   * Converts primitive worksheet values into text without stringifying complex
   * objects.
   */
  function toCellString(value: unknown) {
    if (value === null || value === undefined) {
      return undefined
    }
    if (value instanceof Date) {
      return toIsoDate(value)
    }
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      typeof value === 'bigint'
    ) {
      return String(value)
    }
    return undefined
  }

  /**
   * Narrows mapped rows after missing rows have been discarded.
   */
  function isDefined<T>(value: T | undefined): value is T {
    return value !== undefined
  }
}
